/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable react/button-has-type */
import React, {
  useEffect, useState, useCallback, useRef, useMemo,
} from 'react';
import * as PropTypes from 'prop-types';
import {
  Form, Row, Button, Col, Spin, Divider, DatePicker,
} from 'antd';
import moment from 'moment';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { withRouter, Redirect, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import routeLocations from '../../../../utility/route-locations';
import ViaValidationAlert from '../../../../components/fields/ViaValidationAlert';
import {
  requestLegalDocuments, requestSaveLegalDocument, requestSaveAndPreviewLegalDocument, requestDeleteEditVersionDocument,
} from '../../../../state/ui/configuration/bynx/legalDocumentUiActions';
import { selectLegalDocuments } from '../../../../state/domain/legalDocuments/legalDocumentReducer';
import { legalDocumentTypes, getLegalDocumentTranslationId } from './LegalDocumentUtil';
import { selectLoadLegalDocumentsRequest, selectSaveLegalDocumentRequest } from '../../../../state/ui/configuration/bynx/legalDocumentUiReducer';
import { convertToUTCDate, DATE_FORMAT } from '../packageUtil';
import ConfigHeading from '../../configure/components/ConfigHeading';

// This breaks quill if it is re-rendered. So I pulled it out of the main component.
const modules = {
  toolbar: {
    container: '#toolbar',
  },
  clipboard: {
    matchVisual: false,
  },
};

/**
* This also cannot be re-created on render (it loses all of its functionality with quill, god knows why)
* Custom toolbar component to allow control of what buttons appear.
*/
const CustomToolbar = () => (
  <Row id="toolbar" type="flex" justify="space-between">
    <Col span={8}>
      <button className="ql-bold" />
      <button className="ql-italic" />
      <button className="ql-underline" />
      <button className="ql-list" value="ordered" />
      <button className="ql-list" value="bullet" />
      <button className="ql-link" />
    </Col>
  </Row>
);

// Not sure if this needs to be outside the component, but its similar to modules so I'm not risking it.
const formats = [
  'bold', 'italic', 'underline',
  'list', 'bullet', 'indent',
  'link',
];

/**
 * The field for the effective date.  Allows clear.
 * The rules are injected into the form using the form field decorator.
 */
const StartDateField = injectIntl(({
  form, document, intl,
}) => (
  <Form.Item required label={<FormattedMessage id="startDate" />}>
    { form.getFieldDecorator('effectiveDate', {
      initialValue: document ? moment.utc(document.effectiveDate) : undefined,
      rules: [
        { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
        {
          validator(rule, value, callback) {
            if (moment.utc().isAfter(convertToUTCDate(value))) {
              callback(intl.formatMessage({ id: 'futureDate' }));
            }
            callback();
          },
        },
      ],
    })(
      <DatePicker
        style={{ width: '300px' }}
        disabledDate={value => convertToUTCDate(value).isBefore(moment.utc())}
        format={DATE_FORMAT}
        showToday={false}
        placeholder={intl.formatMessage({ id: 'selectOne' })}
      />,
    )}
  </Form.Item>
));

function EditLegalDocument({
  match, loadPageData, selectDocuments, history, saveContent, saveError, loadingContent, form, saveAndPreview, saving, deleteDocument,
}) {
  const docType = match.params.id;
  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  const documents = selectDocuments(docType);

  const intl = useIntl();

  const editableDocument = documents
    ? documents.find(document => !documents.find(doc2 => doc2.versionNumber > document.versionNumber))
    : undefined;
  // The quill instance contains all methods to interact with the text editor.
  const quillRef = useRef();

  // The html produced by the editor. Allows programatic changing of the content with html (I.E restore default)
  const [currentHtml, setCurrentHtml] = useState(null);

  // Set the default html when we first load the document content.
  useEffect(() => { if (!currentHtml) setCurrentHtml(editableDocument ? editableDocument.content : null); },
    [editableDocument, setCurrentHtml, currentHtml]);

  // Gets the content of the currently active document.
  const defaultContent = useMemo(() => {
    if (!documents) return null;
    const activeDocuments = documents.slice().filter(doc => doc.active && moment.utc(doc.effectiveDate).isBefore());
    const activeDocument = activeDocuments.find(
      doc => !activeDocuments.find(
        laterDoc => doc.versionNumber < laterDoc.versionNumber,
      ),
    );
    return activeDocument ? activeDocument.content : null;
  }, [documents]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    form.validateFields((err, values) => {
      if (!err) {
        saveContent({
          documentType: docType,
          content: currentHtml,
          effectiveDate: moment.utc(values.effectiveDate).startOf('d').toISOString(),
          active: false,
        }, history, intl);
      }
    });
  }, [history, saveContent, form, currentHtml, docType, intl]);

  const saveAndPreviewDocument = useCallback(() => {
    form.validateFields((err, values) => {
      if (!err) {
        saveAndPreview({
          documentType: docType,
          content: currentHtml,
          effectiveDate: moment.utc(values.effectiveDate).startOf('d').toISOString(),
          active: false,
        }, intl);
      }
    });
  }, [form, currentHtml, docType, intl, saveAndPreview]);

  if (quillRef.current) {
    // For some reason, even with images removed from the formats list, you can still drag and drop them into the editor.
    // This prevents such shenanigans.
    quillRef.current.editor.container.ondragover = () => false;
  }

  // Remove them if they have an invalid document type
  if (!docType || !Object.values(legalDocumentTypes).find(validType => validType === docType)) {
    return <Redirect to={routeLocations.notFound} />;
  }

  // The main component
  return (
    <ConfigHeading
      titleId={`${getLegalDocumentTranslationId(docType)}`}
      links={[
        (<Link to={routeLocations.admin_legal_documents}><FormattedMessage id="returnToSummary" /></Link>),
      ]}
    >
      <Form onSubmit={e => handleSubmit(e)}>
        <Divider style={{ marginTop: 0 }} />
        <ViaValidationAlert validationError={saveError} className="quill-style" />
        <div className="quill-style" style={{ marginTop: '40px' }}>
          <CustomToolbar />
          {!loadingContent ? (
            <ReactQuill
              style={{ margin: '0px' }}
              ref={quillRef}
              formats={formats}
              value={currentHtml}
              modules={modules}
              onChange={(content, delta, source, editor) => { setCurrentHtml(editor.getHTML()); }}
            />
          ) : <Spin size="large" style={{ margin: '20px' }} />}
          <StartDateField form={form} document={editableDocument} />
          <Form.Item>
            <Row type="flex" justify="end">
              <Button
                style={{ margin: '0.5em' }}
                type="danger"
                disabled={!editableDocument || (editableDocument.active && moment.utc().isAfter(moment.utc(editableDocument.effectiveDate)))}
                onClick={() => { deleteDocument(docType, history); }}
              >
                <FormattedMessage id="delete" />
              </Button>
              <Button
                style={{ margin: '0.5em' }}
                disabled={!defaultContent}
                onClick={() => { setCurrentHtml(defaultContent); }}
              >
                <FormattedMessage id="revertToDefault" />
              </Button>
              <Button
                style={{ margin: '0.5em' }}
                loading={saving}
                onClick={() => { saveAndPreviewDocument(); }}
              >
                <FormattedMessage id="save-and-preview" />
              </Button>
              <Button
                style={{ margin: '0.5em' }}
                type="primary"
                htmlType="submit"
                loading={saving}
              >
                <FormattedMessage id="save-and-exit" />
              </Button>
            </Row>
          </Form.Item>
        </div>
      </Form>
    </ConfigHeading>
  );
}
EditLegalDocument.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  loadPageData: PropTypes.func.isRequired,
  form: PropTypes.shape({
    validateFields: PropTypes.func.isRequired,
    getFieldDecorator: PropTypes.func.isRequired,
  }).isRequired,
  saveError: PropTypes.shape({
    errorMessage: PropTypes.string.isRequired,
    code: PropTypes.number.isRequired,
    errors: PropTypes.arrayOf(PropTypes.string),
  }),
  history:
    PropTypes.shape({
      push: PropTypes.func.isRequired,
      goBack: PropTypes.func.isRequired,
    }).isRequired,
  saveContent: PropTypes.func.isRequired,
  selectDocuments: PropTypes.func.isRequired,
  saveAndPreview: PropTypes.func.isRequired,
  loadingContent: PropTypes.bool,
  saving: PropTypes.bool,
  deleteDocument: PropTypes.func.isRequired,
};

EditLegalDocument.defaultProps = {
  saveError: null,
  loadingContent: false,
  saving: false,
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const saveRequest = selectSaveLegalDocumentRequest(state);
  const loadingRequest = selectLoadLegalDocumentsRequest(state);
  return {
    saveError: saveRequest ? saveRequest.error : null,
    saving: saveRequest ? saveRequest.isLoading : false,
    selectDocuments: docType => selectLegalDocuments(state, docType),
    loadingContent: loadingRequest ? loadingRequest.isLoading : false,
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  loadPageData: () => dispatch(requestLegalDocuments()),
  saveContent: (document, history, intl) => dispatch(requestSaveLegalDocument(document, history, intl)),
  saveAndPreview: (document, intl) => dispatch(requestSaveAndPreviewLegalDocument(document, intl)),
  deleteDocument: (documentType, history) => dispatch(requestDeleteEditVersionDocument(documentType, history)),
});

/**
 * Connect the redux state to our component.
 */
export default connect(mapStateToProps, mapDispatchToProps)(
  injectIntl(
    withRouter(
      Form.create({ name: 'edit_legal_document' })(EditLegalDocument),
    ),
  ),
);
